import { useEffect, useRef, FC } from "react";
import { useTranslation } from "react-i18next";
import Auth from "./Auth";
import { useNavigate } from "react-router-dom";
import { AuthState } from "../appinitandstate/AuthState";

interface TokenRefreshProps {
  auth: Auth;
  pollIntervalMs?: number;
  isOnline?: () => boolean; // for testing/offline override, defaults to navigator.onLine
}

const DEFAULT_POLL_INTERVAL_MS = 60_000; // 1 minute
const IN_FLIGHT_SAFETY_TIMEOUT_MS = 30_000; // 30s safety reset

/**
 * Periodically checks whether the auth token needs refreshing.
 * Simple polling loop – no event handlers, no complex scheduling.
 *
 * On each tick:
 *  1. Skip if a refresh is already in flight.
 *  2. Skip if no refresh timestamp is set (not logged in).
 *  3. Skip if offline (next tick will check again).
 *  4. If refreshTokenAfter has passed → call auth.renewSession().
 *     - Success: next tick will see the updated refreshTokenAfter.
 *     - SessionExpired: alert the user and log out.
 *     - Failed (transient): do nothing, next tick will retry.
 */
export const TokenRefreshComponent: FC<TokenRefreshProps> = ({
  auth,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  isOnline,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inFlightRef = useRef(false);
  const safetyTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const online = () =>
      isOnline
        ? isOnline()
        : typeof navigator !== "undefined"
          ? navigator.onLine
          : true;

    const tick = () => {
      // Guard: don't fire parallel refreshes
      if (inFlightRef.current) return;

      const refreshAfter = auth.getRefreshTokenAfter();
      // No token / not logged in → nothing to do
      if (refreshAfter === undefined) return;
      // Offline → skip, next tick will check again
      if (!online()) return;
      // Token still has enough time left → nothing to do
      if (Date.now() < refreshAfter) return;

      // Time to refresh
      inFlightRef.current = true;

      // Safety: if the auth0-js callback never fires (e.g. internal crash),
      // forcefully unlock after 30s so the next tick can retry.
      safetyTimerRef.current = window.setTimeout(() => {
        if (inFlightRef.current) {
          console.warn(
            "Token refresh safety timeout reached. Resetting inFlight guard.",
          );
          inFlightRef.current = false;
        }
        safetyTimerRef.current = null;
      }, IN_FLIGHT_SAFETY_TIMEOUT_MS);

      auth.renewSession((result) => {
        inFlightRef.current = false;
        // Clear safety timer since callback fired normally
        if (safetyTimerRef.current !== null) {
          window.clearTimeout(safetyTimerRef.current);
          safetyTimerRef.current = null;
        }

        if (result === AuthState.OkWithOnlineAuthentication) {
          console.info("Token refreshed successfully.");
        } else if (result === AuthState.SessionExpired) {
          console.warn("Session expired. Logging out.");
          alert(t("auth.sessionExpiredAlert"));
          auth.logout(navigate);
        } else {
          console.warn("Token refresh failed, will retry on next tick.");
        }
      });
    };

    // Run once immediately on mount, then periodically
    tick();
    const intervalId = window.setInterval(tick, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
      if (safetyTimerRef.current !== null) {
        window.clearTimeout(safetyTimerRef.current);
      }
    };
  }, [auth, navigate, pollIntervalMs, isOnline, t]);

  // This component doesn't render anything
  return null;
};
